import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  LinearProgress,
  Link,
  IconButton,
} from '@mui/material';
import SendTemplateForm from '../../Template/sendTemplateForm';
import FileInput from '../../../Shared/FileUploadPreview/FileInput';
import { useRecoilValue } from 'recoil';
import { WhatsAppTempFormAtom } from '../../../../Services/_states';
import { TemplateActions } from '../../../../Services/_actions';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '../../../Shared/Loading/index.tsx';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import ModalForms from '../../../Shared/Modals/index.js';
import { BLOCKED_EXTENSIONS } from '../../Template/template.constant.js';

interface SendTemplateModalProps {
  open: boolean;
  onClose: () => void;
}

const SendTemplateModal: React.FC<SendTemplateModalProps> = ({ open, onClose }) => {
  const { showSnackBar } = useSnackBar();
  const [progressBar, setProgressBar] = useState(0);
  const [tempDataFile, setTempDataFile] = useState < any > (null);
  const [isValidFile, setIsValidFile] = useState(true);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const whatsappTempFormData: any = useRecoilValue(WhatsAppTempFormAtom);
  const actions = TemplateActions();
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);

const getExt = (name: string) => {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
};


  const downLoadSampleFile = async () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/v0/ui/whatsapp/download-template-sample-sheet/?id=${whatsappTempFormData?.wa_Id}`;
  };

  // const onFileSelect = async (e) => {
  //   const selectedFile = e.target.files[0];
  //   setTempDataFile(selectedFile);
  // };

const onFileSelect = async (e) => {
  const selectedFile = e.target.files[0];

  if (selectedFile) {
    // NEW: block dangerous extensions
    const ext = getExt(selectedFile.name);
    if (BLOCKED_EXTENSIONS.has(ext)) {
      showSnackBar(`Files with "${ext}" extension are not allowed.`,"error");
      e.target.value = '';
      setTempDataFile(null);
      return;
    }

    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel'                                          // .xls
    ];

    if (allowedTypes.includes(selectedFile.type)) {
      setTempDataFile(selectedFile);
    } else {
      showSnackBar('Only Excel (.xlsx) files are allowed.',"error");
      e.target.value = '';
      setTempDataFile(null);
    }
  }
};

  const onFileRemove = (e) => {
    setTempDataFile(null);
    setProgressBar(0);
  };

  const onUploadProgress = (progressEvent) => {
    const { loaded, total } = progressEvent;
    const percent = Math.floor((loaded * 100) / total);
    setProgressBar(percent);
  };

  const onFileUpload = async () => {
    setIsUploadLoading(true);
    setUserCount(0);
    try {
      let formData = new FormData();
      formData.append('file', tempDataFile);
      let res = await actions.uploadUserData(formData, whatsappTempFormData, onUploadProgress);
      if (res?.status === 200) {
        setUserCount(res?.data?.data?.updated_count || 0);
        setQueueCount(res?.data?.data?.queue_count || 0)
        setIsValidFile(true);
        onFileRemove(tempDataFile);
        showSnackBar('File Uploaded Successfully', 'success');
      } else if (res?.status === 400) {
        setIsValidFile(false);
        if (res?.response?.data?.data?.general_error?.errors) {
          showSnackBar(`${res?.response?.data?.data?.general_error?.errors}`, 'error');
        } else {
          showSnackBar('Failed to Upload Data', 'error');
        }
      }
    } catch (error) {
      setIsValidFile(false);
      showSnackBar(error?.toString(), 'error');
    } finally {
      setIsUploadLoading(false);
    }
  };
  const sendTemplateCron = async () => {
    setIsSendLoading(true);
    if (!whatsappTempFormData?.wa_Id) {
      showSnackBar('Template id is missing', 'warning');
      return;
    }
    try {
      let res = await actions.sendTemplateCron(whatsappTempFormData?.wa_Id);
      if (res?.status === 200) {
        setUserCount(0);
        setQueueCount(0);
        onFileRemove(tempDataFile);
        showSnackBar('Template Send Successfully', 'success');
      } else {
        showSnackBar('Error occured while sending templates.Please try again!', 'error');
      }
    } catch (error) {
      showSnackBar(error?.toString(), 'error');
    } finally {
      setIsSendLoading(false);
    }
  };

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalBody={<>
        <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <FileInput
              onFileSelect={onFileSelect}
              onFileRemove={onFileRemove}
              // onFileUpload={onFileUpload}
              file={tempDataFile}
              setFile={setTempDataFile}
              accept=".xlsx,.xls"
            />
            {tempDataFile && (
              <LoadingButton
                onClickButton={onFileUpload}
                buttonName="Upload"
                isDisabled={isUploadLoading}
                isLoading={isUploadLoading}
              />
            )}
          </Box>
          {userCount > 0 && (
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '16px' }}>
              <Typography variant="body1">
                Users to Send Template: <strong>{userCount || 0}</strong>
              </Typography>
              <LoadingButton
                onClickButton={sendTemplateCron}
                buttonName="Send"
                isDisabled={userCount === 0}
                isLoading={isSendLoading}
              />
            </Box>
          )}
          {isSendLoading && queueCount > 0 &&
            <Box>
              <Typography variant='body1'>{`Successfully queued ${queueCount} templates for sending.`}</Typography>
            </Box>
          }
          {progressBar > 0 && (
            <Box sx={{ pt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progressBar}
                sx={{
                  '& .MuiLinearProgress-bar': {
                    bgcolor: isValidFile ? 'primary.main' : 'error.main',
                  },
                }}
              />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                {`${progressBar}%`}
              </Typography>
            </Box>
          )}
          <Link
            component="button"
            variant="body2"
            onClick={downLoadSampleFile}
            sx={{ mt: 2, display: 'block' }}
          >
            Download sample file
          </Link>
        </Box>
        <Box sx={{ pt: 2 }}>
          <SendTemplateForm handleCloseModal={onClose}/>
        </Box>
      </>}
      modalHeader="Send Template"
      modalId="send-template-modal"
      size="md"
    />
  );
};

export default SendTemplateModal;
